<script lang="ts" setup>
const { $t } = useLocalization();

const modelInputs = defineModel<{ [key: string]: any }>('modelInputs', { required: true });

defineProps<{
    modelSchemaFields: any[];
}>();

const fieldTranslations: { [key: string]: { title?: string } } = {
    seed: { title: $t('[[[Ziarno]]]') },
    width: { title: $t('[[[Szerokość]]]') },
    height: { title: $t('[[[Wysokość]]]') },
    prompt: { title: $t('[[[Prompt]]]') },
    image: { title: $t('[[[Obraz]]]') },
    input_image: { title: $t('[[[Obraz wejściowy]]]') },
    input_images: { title: $t('[[[Obrazy wejściowe]]]') },
    aspect_ratio: { title: $t('[[[Proporcje obrazu]]]') },
    output_format: { title: $t('[[[Format wyjściowy]]]') },
    output_quality: { title: $t('[[[Jakość wyjściowa]]]') },
    quality: { title: $t('[[[Jakość]]]') },
    user_id: { title: $t('[[[ID użytkownika]]]') },
    background: { title: $t('[[[Tło]]]') },
    moderation: { title: $t('[[[Moderacja]]]') },
    input_fidelity: { title: $t('[[[Wierność wejścia]]]') },
    openai_api_key: { title: $t('[[[Klucz API OpenAI]]]') },
    number_of_images: { title: $t('[[[Liczba obrazów]]]') },
    output_compression: { title: $t('[[[Kompresja wyjściowa]]]') },
    go_fast: { title: $t('[[[Tryb szybki]]]') },
    guidance: { title: $t('[[[Prowadzenie]]]') },
    guidance_scale: { title: $t('[[[Skala prowadzenia]]]') },
    megapixels: { title: $t('[[[Megapiksele]]]') },
    num_outputs: { title: $t('[[[Liczba wyników]]]') },
    prompt_strength: { title: $t('[[[Siła promptu]]]') },
    num_inference_steps: { title: $t('[[[Liczba kroków wnioskowania]]]') },
    inference_steps: { title: $t('[[[Kroki wnioskowania]]]') },
    disable_safety_checker: { title: $t('[[[Wyłącz sprawdzanie bezpieczeństwa]]]') },
    safety_tolerance: { title: $t('[[[Tolerancja bezpieczeństwa]]]') },
    prompt_upsampling: { title: $t('[[[Ulepszanie promptu]]]') },
    intermediate_timesteps: { title: $t('[[[Pośrednie kroki czasowe]]]') }
};

const getFieldTitle = (field: any): string =>
{
    const translation = fieldTranslations[field.key];

    if (translation?.title)
    {
        return translation.title;
    }

    return field.title || field.key;
};

const getStep = (field: any): number =>
{
    if (field.type === 'integer') return 1;

    return 0.1;
};
</script>

<template>
    <IdeoFormGroup nospace>
        <div class="alert alert-light mb-0">
            <div v-if="true">
                <div v-for="field in modelSchemaFields" :key="field.key" class="mb-1">
                    <template v-if="field.key !== 'prompt'">
                        <label v-if="field.type !== 'array'" :for="field.key" class="form-label">
                            {{ getFieldTitle(field) }}
                            <i v-if="field.description" class="fas fa-regular fa-circle-info ms-2 text-muted" data-bs-toggle="tooltip" :title="field.description" />
                        </label>
                        <template v-if="field.type === 'number' || field.type === 'integer'">
                            <template v-if="field.minimum !== undefined && field.maximum !== undefined">
                                <div class="d-flex align-items-center gap-2">
                                    <input
                                        :id="field.key"
                                        v-model.number="modelInputs[field.key]"
                                        type="range"
                                        class="form-range flex-grow-1"
                                        :min="field.minimum"
                                        :max="field.maximum"
                                        :step="getStep(field)"
                                    >
                                    <input
                                        v-model.number="modelInputs[field.key]"
                                        type="number"
                                        class="form-control"
                                        style="width: 80px"
                                        :min="field.minimum"
                                        :max="field.maximum"
                                        :step="getStep(field)"
                                    >
                                </div>
                            </template>
                            <template v-else>
                                <input
                                    :id="field.key"
                                    v-model.number="modelInputs[field.key]"
                                    type="number"
                                    class="form-control"
                                    :step="getStep(field)"
                                >
                            </template>
                        </template>
                        <template v-else-if="field.type === 'boolean'">
                            <div>
                                <input
                                    :id="field.key"
                                    v-model="modelInputs[field.key]"
                                    type="checkbox"
                                    class="form-check-input"
                                >
                            </div>
                        </template>
                        <template v-else-if="field.allOf && (field.allOf[0].enum || field.allOf[0].$ref)">
                            <select
                                :id="field.key"
                                v-model="modelInputs[field.key]"
                                class="form-select"
                            >
                                <option v-if="field.default" :value="field.default" selected>
                                    {{ field.default }}
                                </option>
                                <template v-if="field.allOf[0].enum">
                                    <option v-for="option in field.allOf[0].enum" :key="option" :value="option">
                                        {{ option }}
                                    </option>
                                </template>
                            </select>
                        </template>
                        <select
                            v-else-if="field.enum"
                            :id="field.key"
                            v-model="modelInputs[field.key]"
                            class="form-select"
                        >
                            <option v-for="option in field.enum" :key="option" :value="option">
                                {{ option }}
                            </option>
                        </select>
                        <template v-else-if="field.type === 'string'">
                            <input
                                v-if="field.format === 'password'"
                                :id="field.key"
                                v-model="modelInputs[field.key]"
                                type="text"
                                class="form-control"
                                :placeholder="field.description || 'Enter api key'"
                            >
                            <input
                                v-else
                                :id="field.key"
                                v-model="modelInputs[field.key]"
                                type="text"
                                class="form-control"
                                :placeholder="field.description || field.title"
                            >
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </IdeoFormGroup>
</template>
